import React, { useState } from 'react';
import type { State as TextareaState } from '../form.styles';
import { showMaxCharWarning, showMaxCharError, showMinCharError } from '../util/showMessageType';
import { StyledTextArea } from './TextareaFieldV2.styles';
import {
  Asterisk,
  FormLabel,
  renderLink,
  MessageContainer,
  HelperText,
  CharCountMessage,
} from '../form.styles';
import Message from '../common/Message';

interface LabelLinkProps {
  url: string;
  copy: string;
}

export interface TextAreaFieldV2Props extends React.HTMLProps<HTMLTextAreaElement> {
  /** Required for accessibility with label */
  id?: string;
  /** Classname for container <div>. For classname passed to <input>, use standard `className` prop */
  containerClassname?: string;
  /** Required for accessibility with label. An id is also required for the label to appear. */
  label?: string;
  /** default value for the textarea. */
  defaultValue?: string;
  /** Shows red asterisk on label. Client responsible for adding required validation */
  required?: boolean;
  /** Additional information to provide context about the field shown below the text area */
  helperText?: string;
  /** Error message shown below field; passed in by rff or redux form validation */
  errorMessage?: React.ReactNode;
  /** Link or element shown beside label for desktop widths and below textarea for non-desktop widths */
  labelLink?: LabelLinkProps | React.ReactElement;
  value?: string;
  /** Shows character count in lower right part of input and cycles through warning, error states as user inputs and nears limit */
  maxChars?: number;
}

/**
 * Important note: the onChange prop does not behave like one would expect.
 * It overwrites the existing onChange function entirely. This behavior is left in
 * for backwards compatibility with existing usages of the component.
 * If you want to do something *in addition* to the component acting normally on change,
 * use the `onChangeCB` prop.
 */

const TextareaFieldV2: React.FC<TextAreaFieldV2Props> = ({
  // Props here also get passed to the input element
  id,
  required,
  defaultValue,
  value: externalValue = '', // like react-final-form
  label,
  // Props here do not get passed to the input
  containerClassname,
  labelLink,
  maxChars,
  minLength = 0,
  disabled,
  helperText,
  errorMessage,
  onChange,
  onBlur,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  as,
  ...textareaAttributes
}) => {
  const initialInputValue = externalValue || defaultValue || '';
  const [inputValue, setInputValue] = useState(initialInputValue);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);

    if (onChange) {
      onChange(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (onBlur) {
      onBlur(e);
    }
  };

  const maxInvalid = Boolean(maxChars && showMaxCharError(maxChars, inputValue.length));
  const maxWarning = Boolean(maxChars && showMaxCharWarning(maxChars, inputValue.length, 20));
  const minInvalid = Boolean(minLength && showMinCharError(minLength, inputValue.length));

  const Label =
    label && id ? (
      <FormLabel htmlFor={id}>
        {label}
        {required && <Asterisk>*</Asterisk>}
      </FormLabel>
    ) : null;

  const getTextareaState = (): TextareaState => {
    if (errorMessage || maxInvalid || minInvalid) {
      return 'error';
    }
    if (maxWarning) {
      return 'warn';
    }
    return '';
  };

  const renderMessageSection = () => {
    const renderMessage = () =>
      errorMessage || helperText || maxChars ? (
        <>
          {errorMessage ? <Message type="error" text={errorMessage} id={`error-${id}`} /> : null}
          {helperText && !errorMessage ? <HelperText>{helperText}</HelperText> : null}
          {maxChars ? (
            <CharCountMessage state={getTextareaState()}>
              {inputValue.length}/{maxChars}
            </CharCountMessage>
          ) : null}
        </>
      ) : null;
    const message = renderMessage();
    return message ? <MessageContainer>{message} </MessageContainer> : null;
  };

  return (
    <div className={containerClassname}>
      {Label}
      {renderLink('label', true, labelLink)}
      <StyledTextArea
        {...textareaAttributes}
        value={inputValue}
        id={id}
        aria-required={required}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        // styling prop
        state={getTextareaState()}
        aria-describedby={errorMessage ? `error-${id}` : undefined}
      />
      {renderMessageSection()}
      {renderLink('input', true, labelLink)}
    </div>
  );
};

export default TextareaFieldV2;
